import React, { useContext, useEffect, useState } from 'react';
import FlexColumn from '../components/shared/FlexColumn';
import FlexRow from '../components/shared/FlexRow';
import UserContext from '../context/UserContext';
import { apiConfig, DISTRIBUTION_URL } from '../utils/apiConfig';
import { API } from '../utils/API';

const emptyBanner = { mainImage: '', modalImage: '' };

export default function Graphics({ token }) {
  const { role } = useContext(UserContext);
  const isAdmin = role === 'admin';
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const [banner1, setBanner1] = useState({ ...emptyBanner });
  const [banner2, setBanner2] = useState({ ...emptyBanner });
  const [saving, setSaving] = useState(false);

  const uploadFile = async (file, slot) => {
    try {
      const ts = new Date().getTime();
      const fileName = `${ts}_${slot}.jpg`;
      const directory = 'modalBanners';

      const presignedUrlResponse = await fetch(`${apiConfig.media.uploadImage}/${directory}/${fileName}/jpeg`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!presignedUrlResponse.ok) {
        throw new Error(`Failed to get pre-signed URL: ${presignedUrlResponse.statusText}`);
      }

      const presignedUrl = await presignedUrlResponse.text();
      const response = await fetch(presignedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'image/jpeg' },
        body: file,
      });

      if (!response.ok) {
        throw new Error(`File upload failed: ${response.statusText}`);
      }

      const key = `${directory}/${fileName}`;
      return key;
    } catch (e) {
      console.error('[Graphics] [uploadFile] Error:', e);
      return '';
    }
  };

  const handleFileChange = async (e, slot) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingSlot(slot);
    const key = await uploadFile(file, slot);
    setUploadingSlot(null);

    if (slot === 'banner1_main') {
      setBanner1((prev) => ({ ...prev, mainImage: key }));
    } else if (slot === 'banner1_modal') {
      setBanner1((prev) => ({ ...prev, modalImage: key }));
    } else if (slot === 'banner2_main') {
      setBanner2((prev) => ({ ...prev, mainImage: key }));
    } else if (slot === 'banner2_modal') {
      setBanner2((prev) => ({ ...prev, modalImage: key }));
    }
  };

  const fetchModalBanners = async () => {
    const res = await API(apiConfig.forumSettings.getModalBanners);
    if (res.status && res.modalBanners) {
      setBanner1(res.modalBanners.banner1 ?? { ...emptyBanner });
      setBanner2(res.modalBanners.banner2 ?? { ...emptyBanner });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const body = { banner1, banner2 };
    const res = await API(apiConfig.forumSettings.updateModalBanners, 'POST', body);

    if (res.status) {
      alert('נשמר בהצלחה!');
    } else {
      alert('שמירה נכשלה.');
    }
    setSaving(false);
  };

  useEffect(() => {
    fetchModalBanners();
  }, []);

  const renderSlot = (label, path, slot) => (
    <div key={slot} style={styles.slot}>
      <label style={styles.label}>{label}</label>
      {path && <img src={`${DISTRIBUTION_URL}${path}`} alt={label} style={styles.imagePreview} />}
      {isAdmin && (
        <>
          <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, slot)} disabled={!!uploadingSlot} />
          {uploadingSlot === slot && <p style={styles.uploading}>מעלה תמונה...</p>}
        </>
      )}
    </div>
  );

  return (
    <FlexColumn expand={styles.container}>
      <h2 style={styles.heading}>באנרים בעמוד הבית</h2>

      <h3 style={styles.subHeading}>באנר 1</h3>
      <FlexRow style={styles.slotsRow}>
        {renderSlot('תמונה ראשית', banner1.mainImage, 'banner1_main')}
        {renderSlot('תמונה במודל', banner1.modalImage, 'banner1_modal')}
      </FlexRow>

      <h3 style={styles.subHeading}>באנר 2</h3>
      <FlexRow style={styles.slotsRow}>
        {renderSlot('תמונה ראשית', banner2.mainImage, 'banner2_main')}
        {renderSlot('תמונה במודל', banner2.modalImage, 'banner2_modal')}
      </FlexRow>

      {isAdmin && (
        <button style={styles.saveButton} onClick={handleSave} disabled={saving}>
          {saving ? 'שומר...' : 'שמירה'}
        </button>
      )}
    </FlexColumn>
  );
}

const styles = {
  container: {
    padding: 30,
    paddingBottom: 150,
    maxWidth: 600,
    margin: 'auto',
    fontFamily: 'sans-serif',
  },
  heading: {
    marginBottom: 16,
    fontSize: 22,
    fontWeight: 600,
  },
  subHeading: {
    marginTop: 24,
    marginBottom: 12,
    fontSize: 18,
    fontWeight: 600,
  },
  slot: {
    marginBottom: 16,
    marginRight: 24,
  },
  slotsRow: {
    flexWrap: 'wrap',
  },
  label: {
    display: 'block',
    fontSize: 14,
    fontWeight: 500,
    marginBottom: 6,
  },
  imagePreview: {
    width: 200,
    borderRadius: 10,
    marginTop: 6,
    marginBottom: 8,
    boxShadow: '0 0 8px rgba(0,0,0,0.1)',
  },
  uploading: {
    margin: 0,
    fontSize: 14,
    color: '#666',
  },
  saveButton: {
    backgroundColor: '#8f3cc0',
    color: '#fff',
    padding: '12px 20px',
    borderRadius: 24,
    border: 'none',
    fontWeight: 600,
    fontSize: 16,
    cursor: 'pointer',
    marginTop: 24,
  },
};
